import React, { useRef, useCallback } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { TOKENS } from '../lib/designTokens';
import { services, introContent } from '../data/mock';
import { getText, getStyle } from '../config/overrides';
import { getState2GlassVars } from '../utils/state2Glass';

const useReducedMotion = () => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
};

const isTouchDevice = () =>
  typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const GlassCard = ({ title, description, cta, onClick, reducedMotion, editPrefix, glassId, adminOverrides }) => {
  const cardRef = useRef(null);

  // Glass overrides come from a SEPARATE key (state2.card1.glass / state2.card2.glass),
  // NOT from the card's own editPrefix. This keeps glass settings isolated.
  const glassRaw = glassId ? getStyle(glassId) : undefined;
  const glassVars = getState2GlassVars(glassRaw);

  // Per-element style overrides (font, color, etc.) come from the card's editPrefix
  const cardStyle = editPrefix ? getStyle(editPrefix) : undefined;

  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  const smoothX = useSpring(mouseX, { stiffness: 150, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 150, damping: 20 });

  const glareX = useTransform(smoothX, (v) => `${v * 100}%`);
  const glareY = useTransform(smoothY, (v) => `${v * 100}%`);
  const glareOpacity = useMotionValue(0);
  const smoothGlareOpacity = useSpring(glareOpacity, { stiffness: 200, damping: 25 });

  const handleMouseMove = useCallback((e) => {
    if (reducedMotion || isTouchDevice()) return;
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    mouseX.set((e.clientX - rect.left) / rect.width);
    mouseY.set((e.clientY - rect.top) / rect.height);
    glareOpacity.set(1);
  }, [reducedMotion, mouseX, mouseY, glareOpacity]);

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0.5);
    mouseY.set(0.5);
    glareOpacity.set(0);
  }, [mouseX, mouseY, glareOpacity]);

  return (
    <motion.div
      ref={cardRef}
      className="glass-card"
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid={`card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      data-edit-id={editPrefix || undefined}
      style={{ ...cardStyle, ...glassVars }}
    >
      {/* Hype4 specular overlay — 2nd layer for inset glow */}
      <div className="glass-card__specular" aria-hidden="true" />
      {!reducedMotion && (
        <motion.div
          className="glass-card__glare"
          style={{
            '--glare-x': glareX,
            '--glare-y': glareY,
            opacity: smoothGlareOpacity,
          }}
          aria-hidden="true"
        />
      )}
      <div className="glass-card__content">
        <h3
          className="glass-card__title"
          data-edit-id={editPrefix ? `${editPrefix}.title` : undefined}
          style={editPrefix ? getStyle(`${editPrefix}.title`) : undefined}
        >
          {editPrefix ? getText(`${editPrefix}.title`, title) : title}
        </h3>
        <p
          className="glass-card__desc"
          data-edit-id={editPrefix ? `${editPrefix}.desc` : undefined}
          style={editPrefix ? getStyle(`${editPrefix}.desc`) : undefined}
        >
          {editPrefix ? getText(`${editPrefix}.desc`, description) : description}
        </p>
        <button
          className="glass-card__cta"
          onClick={(e) => { e.stopPropagation(); onClick?.(); }}
          aria-label={`${cta} — ${title}`}
          data-testid={`cta-${title.toLowerCase().replace(/\s+/g, '-')}`}
          data-edit-id={editPrefix ? `${editPrefix}.cta` : undefined}
          style={editPrefix ? getStyle(`${editPrefix}.cta`) : undefined}
        >
          <span>{editPrefix ? getText(`${editPrefix}.cta`, cta) : cta}</span>
          <ArrowRight size={16} strokeWidth={1.5} className="glass-card__cta-icon" />
        </button>
      </div>
    </motion.div>
  );
};

export const ContentCards = ({ onOpenDetail, onOpenQuote, adminOverrides }) => {
  const rm = useReducedMotion();
  // Map card index → glass override key (user-mandated names)
  const glassKeys = ['state2.card1.glass', 'state2.card2.glass'];

  return (
    <div className="content-inner" data-testid="content-cards">
      <h1
        className="content-heading"
        data-edit-id="state2.heading"
        style={getStyle('state2.heading')}
      >
        {getText('state2.heading', introContent.contentHeading)}
      </h1>
      <div className="cards-stack">
        {services.map((s, i) => (
          <motion.div
            key={s.id}
            className={`card-outline-wrap ${i === 0 ? 'card-outline-cw' : 'card-outline-ccw'}`}
            initial={{ opacity: 0, y: rm ? 0 : 18 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={rm ? {} : { y: -4, transition: { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] } }}
            whileTap={rm ? {} : { scale: 0.98, transition: { type: 'spring', stiffness: 400, damping: 25 } }}
            transition={{
              duration: 0.45,
              delay: rm ? 0 : 0.12 + i * 0.1,
              ease: TOKENS.animation.ease.gentle,
            }}
          >
            <GlassCard
              title={s.title}
              description={s.description}
              cta={s.cta}
              onClick={() => onOpenDetail?.(s.id)}
              reducedMotion={rm}
              editPrefix={`state2.card.${s.id}`}
              glassId={glassKeys[i]}
              adminOverrides={adminOverrides}
            />
          </motion.div>
        ))}
      </div>
      <motion.button
        className="quote-cta-subtle"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: rm ? 0 : 0.5, ease: TOKENS.animation.ease.gentle }}
        onClick={() => onOpenQuote?.(null)}
        data-testid="quote-cta-state2"
      >
        Un projet en t&ecirc;te&nbsp;? &rarr;
      </motion.button>
    </div>
  );
};
